import React, { useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { 
  Language, 
  CreateLanguageData, 
  LanguageDirectionEnum, 
  TranslationEnableEnum,
  LanguageAutoTranslateEnum,
  LanguageTranslationMemoryEnum 
} from "../../modules/shared/model/LanguageInterfaces";

interface LanguageModalProps {
  language: Language | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateLanguageData, isEdit: boolean) => Promise<void>;
  formData: any;
  setFormData: (data: any) => void;
}

const LanguageModal: React.FC<LanguageModalProps> = ({
  language,
  isOpen,
  onClose,
  onSubmit,
  formData,
  setFormData
}) => {
  const [activeTab, setActiveTab] = useState("general");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Direction options
  const directionOptions = useMemo(() => [
    { value: LanguageDirectionEnum.ltr, label: "Left to Right (LTR)" },
    { value: LanguageDirectionEnum.rtl, label: "Right to Left (RTL)" }
  ], []);

  // Translation enable options
  const translationEnableOptions = useMemo(() => [
    { value: TranslationEnableEnum.Disabled, label: "Disabled" },
    { value: TranslationEnableEnum.Enabled, label: "Enabled" }
  ], []);

  // Auto translate options
  const autoTranslateOptions = useMemo(() => [
    { value: LanguageAutoTranslateEnum.disable, label: "Disabled" },
    { value: LanguageAutoTranslateEnum.enable, label: "Enabled" }
  ], []);

  // Translation memory options
  const translationMemoryOptions = useMemo(() => [
    { value: LanguageTranslationMemoryEnum.disable, label: "Disabled" },
    { value: LanguageTranslationMemoryEnum.enable, label: "Enabled" }
  ], []);

  // Validation function
  const validateForm = useCallback(() => {
    const errors: Record<string, string> = {};
    
    // Required fields validation
    if (!formData.name?.trim()) {
      errors.name = "Language name is required";
    }
    
    if (!formData.initial?.trim()) {
      errors.initial = "Language initial is required";
    }
    
    if (!formData.direction?.id) {
      errors.direction = "Language direction is required";
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  // Check if form is valid
  const isFormValid = useMemo(() => {
    return formData.name?.trim() && 
           formData.initial?.trim() && 
           formData.direction?.id;
  }, [formData]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    try {
      await onSubmit(formData, !!language);
      setValidationErrors({});
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, language, onSubmit, validateForm]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.95 }}
          className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        >
          <div className="p-4 md:p-6 border-b sticky top-0 bg-white">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {language ? `Edit ${language.name}` : "Add New Language"}
              </h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="p-4 md:p-6">
            {/* Tabs */}
            <div className="flex overflow-x-auto hide-scrollbar space-x-4 border-b mb-6">
              {["general", "content", "settings"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`pb-4 px-2 whitespace-nowrap capitalize ${
                    activeTab === tab
                      ? "border-b-2 border-blue-600 text-blue-600"
                      : "text-gray-500"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="space-y-6">
              {activeTab === "general" && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Language Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.name || ""}
                        onChange={(e) => {
                          setFormData({ ...formData, name: e.target.value });
                          if (validationErrors.name) {
                            setValidationErrors({ ...validationErrors, name: "" });
                          }
                        }}
                        className={`mt-1 block w-full px-3 py-2 border rounded-lg ${
                          validationErrors.name ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                        }`}
                        placeholder="e.g. English"
                      />
                      {validationErrors.name && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Language Initial <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.initial || ""}
                        onChange={(e) => {
                          setFormData({ ...formData, initial: e.target.value });
                          if (validationErrors.initial) {
                            setValidationErrors({ ...validationErrors, initial: "" });
                          }
                        }}
                        className={`mt-1 block w-full px-3 py-2 border rounded-lg ${
                          validationErrors.initial ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                        }`}
                        placeholder="e.g. en"
                      />
                      {validationErrors.initial && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.initial}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Direction <span className="text-red-500">*</span>
                    </label>
                    <select 
                      className={`mt-1 block w-full px-3 py-2 border rounded-lg ${
                        validationErrors.direction ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      }`}
                      value={formData.direction?.id || ""}
                      onChange={(e) => {
                        setFormData({ ...formData, direction: { id: e.target.value } });
                        if (validationErrors.direction) {
                          setValidationErrors({ ...validationErrors, direction: "" });
                        }
                      }}
                    >
                      <option value="">Select Direction</option>
                      {directionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {validationErrors.direction && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.direction}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input 
                        type="checkbox" 
                        className="rounded border-gray-300"
                        checked={formData.isDefault || false}
                        onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                      />
                      <span>Set as default language</span>
                    </label>
                    <p className="text-sm text-gray-500 ml-6">
                      This will be the fallback language when a translation is not available
                    </p>
                  </div>
                </div>
              )}

              {activeTab === "regions" && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Service Categories
                    </label>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">
                        Categories will be managed through the main categories system.
                        You can assign categories to languages after creation.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "content" && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium mb-4">Translation Settings</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Enable Translation</label>
                        <select 
                          className="mt-1 block w-full px-3 py-2 border rounded-lg"
                          value={formData.isTranslationEnable ? TranslationEnableEnum.Enabled : TranslationEnableEnum.Disabled}
                          onChange={(e) => setFormData({ ...formData, isTranslationEnable: e.target.value === TranslationEnableEnum.Enabled })}
                        >
                          {translationEnableOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "settings" && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium mb-4">Advanced Settings</h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Translation Memory
                          </label>
                          <select 
                            className="mt-1 block w-full px-3 py-2 border rounded-lg"
                            value={formData.translationMemory?.id || ""}
                            onChange={(e) => setFormData({ ...formData, translationMemory: { id: e.target.value } })}
                          >
                            <option value="">Select Option</option>
                            {translationMemoryOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Auto Translation
                          </label>
                          <select 
                            className="mt-1 block w-full px-3 py-2 border rounded-lg"
                            value={formData.autoTranslate?.id || ""}
                            onChange={(e) => setFormData({ ...formData, autoTranslate: { id: e.target.value } })}
                          >
                            <option value="">Select Option</option>
                            {autoTranslateOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                          <label className="flex items-center gap-2">
                            <input 
                              type="checkbox" 
                              className="rounded border-gray-300"
                              checked={formData.isAutomaticSync || false}
                              onChange={(e) => setFormData({ ...formData, isAutomaticSync: e.target.checked })}
                            />
                            <span>Automatically sync translations</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input 
                              type="checkbox" 
                              className="rounded border-gray-300"
                              checked={formData.isNotify || false}
                              onChange={(e) => setFormData({ ...formData, isNotify: e.target.checked })}
                            />
                            <span>Notify translators of new content</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 pt-6 border-t flex flex-col sm:flex-row justify-end gap-3">
              <Button variant="outline" onClick={onClose} className="w-full sm:w-auto" disabled={isSubmitting}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit} 
                className="w-full sm:w-auto" 
                disabled={isSubmitting || !isFormValid}
              >
                {isSubmitting ? "Saving..." : (language ? "Update Language" : "Create Language")}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LanguageModal;
